import React, { useEffect, useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { createContent, uploadingContent, uploadingContentProgress, createAssetFromUrl, createAssetMapping, fetchAssetMappings, invalidateAssetUploaded } from 'actions/content.jsx';

import _ from 'lodash'
import cx from 'classnames'
import shortid from 'shortid';

import {
  Dropdown,
  Form,
  Button,
  Modal,
  TabContainer,
  Tab,
  Nav,
  InputGroup
} from 'react-bootstrap'

import StoryboardView from '../Storyboard'

import { EXTERNAL_LIST } from 'constants/assetManagerLists'

import S3Upload from 'react-s3-uploader/s3upload';
import AuthService from 'services/auth.service';
import Dropzone from 'react-dropzone';
import { isUrl, fixHTTPUrl } from 'services/asset.service';
import FileUploadService from 'services/file.upload.service';
import { AssetUploaderFooter } from '../allContents/components';
import StoryboardContext from 'components/Storyboard/context';
import { PaperfliteAssetManager } from '../Paperflite';
import { ContentCard } from '../allContents/components/contentCard';
import FileService from 'services/file.service';
import { createMixpanelEvent } from "actions/mixpanel";
import storyboardRoleService from 'services/storyboardRoles.service';
import { getStoryboardMixpanelEventPayload } from 'services/mixpanelUtil';
import { MIXPANEL_ASSET_UPLOADED } from 'constants/mixpanelEvents';
import styles from './assetUploader.module.scss'
import { INTERACTIVE_CONTENT_TYPES } from 'constants/storyboardType';

const API_BASE_URL = `${process.env.REACT_APP_PAPERFLITE_API_URL}/api/2.0`;

const AssetUploader = ({ handleViewChange, storyboard, className, inline }) => {
  const dispatch = useDispatch();

  const contentUpdated = useSelector(state => _.get(state, 'content.updated', false));

  const [state, setState] = useState({
    dropdown: false,
    showInput: false,
    externalLink: '',
    showUpload: false,
  })

  useEffect(() => {
    if (state.dropdown) {
      handleViewChange(true);
      return
    }
    handleViewChange(false)
  }, [state.dropdown])

  const handleFetchMappings = (page) => {
    if(storyboard && page) {
      var request = {
        storyboard: _.get(storyboard, 'id'),
        excludeStoryboardAssets: inline
      }
      dispatch(fetchAssetMappings(request, page, 20));
    }
  }

  useEffect(() => {
    if (!contentUpdated) {
      return
    }
    handleFetchMappings(0);
  }, [contentUpdated])

  const handleUploadModalToggle = () => {
    setState((state) => {
      return {
        ...state,
        showUpload: !state.showUpload
      }
    })
  }

  return (
    <div className={cx(styles.uploadAssetContainer, className)}>
      <Button className={styles.uploadAssetButton} onClick={() => handleUploadModalToggle()} variant='primary' disabled={!storyboardRoleService.canEditStoryboard(storyboard)}>
        <i className={cx('cleverstory-upload', styles.uploadButtonIcon)}/>
        Upload Content
      </Button>
      <AssetUploadModal
        show={state.showUpload}
        onClose={handleUploadModalToggle}
        inline={inline}
      />
    </div>
  )
}


const AssetUploadModal = ({ show, onClose, inline }) => {
  const dispatch = useDispatch();
  const modalRef = useRef(null);

  const uploadIconRef = React.createRef();
  const contents = useSelector((state) =>
    (state.content.uploads || []).filter(
      (asset) => 
        _.get(asset, 'entityType') != 'url' &&
        (_.get(asset, 'asset.metadata.content_type') !== 'application/url' && 
         _.get(asset, 'asset.source') !== 'paperflite')
    )
  );

  return (
    <Modal show={show} className={styles.assetUploaderContainer} backdropClassName={styles.backdrop} centered>
      <div className={styles.header}>
        <h1 className={styles.title}>Upload Content</h1>
        <div className={styles.actionContainer}>
          {_.size(contents) > 0 &&
            <Button variant='primary' className={styles.uploadComputerButton}>
              <i className={cx('cleverstory-upload', styles.uploadButtonIcon)} />
              <ComputerUpload uploadIconRef={uploadIconRef} />
              Upload From Computer
            </Button>
          }
          <Button variant='info' onClick={onClose} className={styles.closeButton}>
            <i className={cx('cleverstory-close', styles.closeButtonIcon)} />
          </Button>
        </div>
      </div>
      <div className={styles.body} ref={modalRef}>
        <UploaderSectionRender handleModalClose={onClose} inline={inline} container ={modalRef}/>
      </div>
    </Modal>
  )
}

const UploaderSectionRender = ({ handleModalClose, inline, container }) => {
  const dispatch = useDispatch();

  const [state, setState] = useState({
    view: '',
    selection: [],
  })

  const storyboard = useSelector(state => _.get(state, 'storyboards.storyboard.item.id', false));
  const uploadedAssets = useSelector(state => _.get(state, 'content.uploads')) || [];
  const storyboardDetail = useSelector(state => _.get(state, 'storyboards.storyboard.item', []));

  const handleViewChange = (view) => {
    if (!view) {
      return;
    }

    setState((state) => {
      return {
        ...state,
        view: view
      }
    })
  }

  const handleSave = () => {
    if(_.size(uploadedAssets) == 0) {
      return;
    }
    const requestAssetMappings = _.map(uploadedAssets, (asset) => { 
      let entityType = _.get(asset, 'entityType') || _.get(asset, 'asset.entityType'); 
      let request;

      if(entityType === "storyboard" && INTERACTIVE_CONTENT_TYPES.includes(_.get(asset, 'asset.type'))) {
        return {
          type: "PAGE",
          asset: _.get(asset, 'asset.page.id'), 
          source: _.get(asset, 'source')
        }
      }

      if(entityType === "file" || entityType === "asset") {
        request = {
          type: "ASSET"
        }
      } else if(entityType === "page" || entityType === "storyboard") {
        request = {
          type: "PAGE"
        }
      }

      return {
        type: request.type,
        asset: _.get(asset, 'asset.id'), 
        source: _.get(asset, 'source')
      } 
    })
    const request = {
      storyboard: storyboard,
      assetRequests: requestAssetMappings
    }
    dispatch(createAssetMapping(request));
    uploadedAssets.forEach((item) => {
      let assetType = null;
      const fileType = FileService.fileType(_.get(item, 'asset.metadata', null));
      const validTypes = ["LINK", "YOUTUBE", "VIMEO", "WISTIA", "VIDYARD"];
      if (_.includes(validTypes, fileType)) {
      assetType = "Link";
      } else if (item.source === "CLEVERSTORY" && item.type === "PAGE") {
      assetType = "Cleverstory";
      }
      if (assetType) {
        const mixpanelPayload = getStoryboardMixpanelEventPayload(
          storyboardDetail.id,
          storyboardDetail.name,
          storyboardDetail.type
        );
        const updatedPayload = {
          ...mixpanelPayload,
          asset_type: assetType,
        };
        dispatch(createMixpanelEvent(MIXPANEL_ASSET_UPLOADED, updatedPayload));
      }
    });
    handleModalClose();
    dispatch(invalidateAssetUploaded());
  }

  useEffect(() => {
    handleViewChange('allContent');
  }, [])

  return (
    <div className={styles.tabSection}>
      <TabContainer defaultActiveKey={'allContent'} activeKey={state.view} onSelect={handleViewChange}>
        <section className={cx(styles.sideMenu)}>
          <Nav className={styles.sources}>
            <h5>LOCAL</h5>
            <Nav.Item className={cx(styles.source)}>
              <Nav.Link eventKey={'allContent'} className={cx(styles.navButton)}>
                <i className={cx(`cleverstory-laptop`, styles.icon)} />
                <span className={cx(styles.label)}>My Uploads</span>
              </Nav.Link>
            </Nav.Item>
            {!inline &&
              <Nav.Item className={cx(styles.source)} key="myStoryboard">
                <Nav.Link eventKey="myStoryboard" className={cx(styles.navButton)}>
                  <i className={cx(`cleverstory-logo1`, styles.icon)} />
                  <span className={cx(styles.label)}>Cleverstory</span>
                </Nav.Link>
              </Nav.Item>
            }
          </Nav>
          <Nav className={styles.sources}>
            <h5>EXTERNAL</h5>
            {EXTERNAL_LIST.map((list) => {
              return (
                <Nav.Item className={cx(styles.source)} key={list.type}>
                  <Nav.Link eventKey={list.type} className={cx(styles.navButton)}>
                    <i className={cx(`cleverstory-${list.icon}`, styles.icon)} />
                    <span className={cx(styles.label)}>{list.label}</span>
                  </Nav.Link>
                </Nav.Item>
              )
            })}
          </Nav>
        </section>
        <section className={styles.content}>
          <section className={cx(styles.wrapper)}>
            <Tab.Content className={styles.tabContainer}>
              <Tab.Pane className={styles.tabPanes} eventKey="allContent">
                <ComputerUploadRender />
              </Tab.Pane>
              <Tab.Pane className={styles.tabPanes} eventKey="myStoryboard">
                  <StoryboardView
                    selection={state.selection}
                  />
              </Tab.Pane>
              <Tab.Pane className={styles.tabPanes} eventKey="paperflite">
                <PaperfliteAssetManager
                  handleModalClose={handleModalClose}
                  handleSave={handleSave}
                  container={container}
                />
              </Tab.Pane>
              <Tab.Pane className={styles.tabPanes} eventKey="link">
                <ExternalUrlRender />
              </Tab.Pane>
            </Tab.Content>
          </section>
          {state.view !== 'paperflite' &&
            <AssetUploaderFooter
              handleClose={handleModalClose}
              uploadedAssets={uploadedAssets}
              isNotPaperfliteAssest={true}
              handleSave={handleSave}
            />
          }
        </section>
      </TabContainer>
    </div>
  )
}

const ComputerUploadRender = () => {
  const dispatch = useDispatch();
  const uploadIconRef = React.createRef();

  const contents = useSelector((state) =>
    (state.content.uploads || []).filter(
      (asset) => (_.get(asset, 'asset.metadata.content_type') !== 'application/url' && _.get(asset, 'asset.source') !== 'paperflite' && _.get(asset, 'asset.entityType') !== 'page' && _.get(asset, 'asset.entityType') !== 'storyboard')
    )
  );

  const handleFileDrop = (files) => {
    for (var i = 0; i < files.length; i++) {
      var file = files[i];
      file.id = shortid();

      var s3Uploader = new S3Upload({
        signingUrl: "/file_upload/initiate",
        signingUrlMethod: "POST",
        signingUrlHeaders: {
          Authorization: `Bearer ${AuthService.getAccessToken()}`
        },
        server: API_BASE_URL,
        scrubFilename: (filename) => {
          var name = filename.replace(/[^\w\d_\-.\s]+/ig, '')
          return name;
        },
        contentDisposition: 'auto',
        uploadRequestHeaders: {},
        onFinishS3Put: handleContentUploadFinish,
        onProgress: handleContentUploadProgress,
        onError: handleContentUploadError,
        files: [file]
      });

      FileUploadService.trackUpload(s3Uploader);

      dispatch(uploadingContent(file));
    }
  }

  const handleContentUploadFinish = (info, file) => {
    if (info) {
      dispatch(createContent(info.signedUrl, file));
    }
  }

  const handleContentUploadProgress = (progress, textState, file) => {
    dispatch(uploadingContentProgress(file, progress));
  }

  const handleContentUploadError = () => {
    console.log('content upload error!!')
  }

  return (
    <Dropzone noClick={true} onDrop={handleFileDrop}>
      {({ getRootProps, getInputProps, isDragActive }) => (
        <div className={cx(styles.computerUploadContainer, { [styles.emptyContainer]: _.size(contents) == 0 })}>
          {_.size(contents) > 0 &&
            <div className={styles.headerTab}>
              <span className={styles.titleInfo} >Uploaded from Computer</span>
            </div>
          }
          {_.size(contents) == 0 &&
            <div className={styles.actionContainer}>
              <span className={styles.infoTitle} >Drag & Drop files here</span>
              <span className={styles.subText}>or</span>
              <Button variant='primary' className={styles.uploadButton}>
                <ComputerUpload uploadIconRef={uploadIconRef} />
                <i className={cx('cleverstory-upload', styles.icon)} />
                Upload from Computer
              </Button>
            </div>
          }
          <div {...getRootProps({
            className: cx(styles.dropzone, { [styles.active]: isDragActive }, { [styles.emptyView]: _.size(contents) == 0 })
          })}>
            {(contents || []).map((asset, index) => (
              <ContentCard
                key={_.get(asset, 'id', null)}
                id={_.get(asset, 'id', null)}
                assetMapping={asset}
                onClick={() => { console.log('') }}
              />
            ))
            }
          </div>
        </div>
      )}
    </Dropzone>
  )
}

const ExternalUrlRender = () => {
  const dispatch = useDispatch();
  const [value, setValue] = useState("");
  const [error, setError] = useState(null);
  const [showContents, setShowContents] = useState(false);
  const [imported, setImported] = useState(false);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const [displayState, setDisplayState] = React.useState('idle');

  const storyboard = useSelector((state) => _.get(state, `storyboards.storyboard.item.id`, null));
  const urlContents = useSelector((state) =>
    (state.content.uploads || []).filter(
      (asset) => (_.get(asset, 'asset.metadata.content_type') === 'application/url' && _.get(asset, 'asset.source') != 'paperflite')
    )
  );

  const addedUrlAsset = useSelector((state) =>
    (state.content.uploads || []).filter(
      (asset) => (_.get(asset, 'entityType') === 'url' && _.get(asset, 'asset.source') != 'paperflite')
    )
  );
  const uploading = useSelector(state => _.get(state, 'content.uploading', false));
  const uploaded = useSelector(state => _.get(state, 'content.uploaded', false));

  useEffect(() => {
    const newState = uploading ? 'uploading' : imported ? 'imported' : 'idle';
    
    if (newState !== displayState) {
      setIsTransitioning(true);
      
      setTimeout(() => {
        setDisplayState(newState);
        setIsTransitioning(false);
      }, 300);
    }
  }, [uploading, imported]);

  useEffect(() => {
    if (uploaded) {
      setImported(true);
      const timeoutId = setTimeout(() => {
        setImported(false);
        setShowContents(true);
      }, 1000);
      
      return () => clearTimeout(timeoutId);
    } else {
      if ((_.size(urlContents) == 0)) {
        setShowContents(false);
      }
    }
  }, [uploaded]);

  useEffect(() => {
    if (_.size(urlContents) > 1) {
      setShowContents(true);
    }
  }, [urlContents]);

    useEffect(() => {
    if (!uploading) {
      const timeoutId = setTimeout(() => {
        setValue("");
      }, 1000);
  
      return () => clearTimeout(timeoutId);
    }
  }, [uploading]);

  const handleChange = (e) => {
    setValue(e.target.value);
  }

  const handleAddAssetFromUrl = () => {
    var fixedUrl = fixHTTPUrl(value);

    if (!isUrl(fixedUrl)) {
      setError('Please provide a valid url');
      return;
    } else {
      setError(null);
    }

    var request = {
      url: fixedUrl,
      type: 'url',
      storyboard: storyboard
    }

    dispatch(createAssetFromUrl(request));
  }

  return (
    <div className={cx(styles.urlRender, { [styles.emptyUrlWrapper]: !showContents })}>
      <div className={cx(styles.urlActionContainer, { [styles.transitionContainer]: showContents })}>
        <div className={cx(styles.importUrl, {[styles.hasImportUrlContents]: showContents})}>
          <div className={cx(styles.labelContainer, {[styles.hasUrlContents]: showContents})}>
            <span className={styles.label} >Import assets from a URL</span>
            <InputGroup className={styles.inputContainer}>
              <Form.Control
                onChange={handleChange}
                value={value}
                className={styles.inputField}
                type="text"
                placeholder='Paste your URL here'
              />
              {error &&
                <Form.Control.Feedback className={styles.errorValidation} type="invalid">
                  {error}
                </Form.Control.Feedback>
              }
            </InputGroup>
          </div>
          <Button 
            onClick={handleAddAssetFromUrl} 
            variant="primary" 
            className={cx(styles.importButton, {[styles.activeBtn] : (value || (uploading && addedUrlAsset))})} 
            id="button-addon">
              <span className={cx(styles.buttonContent, { 
                [styles.fadeOut]: isTransitioning,
                [styles.fadeIn]: !isTransitioning
              })}>
                {displayState === 'uploading' ? (
                    <>
                      <img src="/images/importArrow.gif" alt="Loading" className={styles.uploadingGif} />
                      Importing...
                    </>
                  ) : displayState === 'imported' ? (
                    <>
                      <i className={cx('cleverstory-tick', styles.uploadButtonIcon)} />
                      Imported
                    </>
                  ) : (
                    <>
                      <i className={cx('cleverstory-import-url', styles.uploadButtonIcon)} />
                      Import URL
                    </>
                  )}
              </span>
          </Button>
        </div>
      </div>
      {showContents &&
        <div className={styles.renderContents}>
          {(urlContents || []).map((asset, index) => (
            <ContentCard
              key={_.get(asset, 'id', null)}
              id={_.get(asset, 'id', null)}
              assetMapping={asset}
              onClick={() => { console.log('') }}
            />
          ))
          }
        </div>
      }
    </div>
  )
}

const ComputerUpload = ({ uploadIconRef }) => {
  const dispatch = useDispatch();

  const handleComputerUpload = (e) => {
    for (var i = 0; i < e.target.files.length; i++) {
      var file = e.target.files[i];
      file.id = shortid();

      var s3Uploader = new S3Upload({
        signingUrl: "/file_upload/initiate",
        signingUrlMethod: "POST",
        signingUrlHeaders: {
          Authorization: `Bearer ${AuthService.getAccessToken()}`
        },
        server: API_BASE_URL,
        scrubFilename: (filename) => {
          let name = filename.replace(/[^\x20-\x7E]/g, '').replace(/[#%&^{}|\\\/]/g, '');
          return name;
        },
        contentDisposition: 'auto',
        uploadRequestHeaders: {},
        onFinishS3Put: handleContentUploadFinish,
        onProgress: handleContentUploadProgress,
        onError: handleContentUploadError,
        files: [file]
      });

      FileUploadService.trackUpload(s3Uploader);

      dispatch(uploadingContent(file));
    }
  };


  const handleContentUploadFinish = (info, file) => {
    if (info) {
      dispatch(createContent(info.signedUrl, file));
    }
  }

  const handleContentUploadProgress = (progress, textState, file) => {
    dispatch(uploadingContentProgress(file, progress));
  }

  const handleContentUploadError = () => {
    console.log('content upload error!!')
  }

  return (
    <Form.Control
      className={cx(styles.fileUpload)}
      ref={uploadIconRef}
      multiple={true}
      onClick={(e) => { e.stopPropagation() }}
      type="file"
      onChange={handleComputerUpload}
    />
  )
}

export default AssetUploader;